import { LocalDate } from "@js-joda/core";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useScrollSyncRegister } from "hooks/useScrollSync";
import {
  formatDayShort,
  formatWeekdayShort,
  getStockholmLocalDateNow,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import { DayCard, DayDate, DayName } from "./scheduleWeekView.styled";
import {
  RowScrollArea,
  RowScrollCard,
  ScheduleRowLayout,
} from "../../common/scheduleRow/scheduleRow.styled";

interface Props {
  currentDate: LocalDate;
  onChangeDate: (date: LocalDate) => void;
  weekDays: LocalDate[];
  onDayClick: (date: LocalDate) => void;
}

const getToday = (): LocalDate => getStockholmLocalDateNow();

export const ScheduleWeekHeaderRow: React.FC<Props> = ({
  currentDate,
  onChangeDate,
  weekDays,
  onDayClick,
}) => {
  const { i18n, t } = useTranslation();
  const today = useMemo(getToday, [currentDate]);
  const registerScrollSync = useScrollSyncRegister();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (registerScrollSync === null) return;
    return registerScrollSync(scrollRef.current);
  }, [registerScrollSync]);

  return (
    <RowScrollCard>
      <RowScrollArea ref={scrollRef}>
        <ScheduleRowLayout $dayCount={7}>
          <PeriodNavigator
            currentDate={currentDate}
            mode="week"
            onChange={onChangeDate}
            showSubLabel
          />
          {weekDays.map((date) => {
            const isToday = date.equals(today);
            return (
              <DayCard
                key={date.toString()}
                $isToday={isToday}
                role="button"
                tabIndex={0}
                aria-label={t("Schedule.OpenDayShifts", {
                  date: `${formatWeekdayShort(date, i18n.language)} ${formatDayShort(date)}`,
                })}
                onClick={() => onDayClick(date)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onDayClick(date);
                  }
                }}
              >
                <DayName>{formatWeekdayShort(date, i18n.language)}</DayName>
                <DayDate $isToday={isToday}>{formatDayShort(date)}</DayDate>
              </DayCard>
            );
          })}
        </ScheduleRowLayout>
      </RowScrollArea>
    </RowScrollCard>
  );
};
